/**
 * Organization Onboarding Wizard
 *
 * 4-step wizard for creating organizations with owners
 * Replaces the old workflow of jumping between tabs
 *
 * Steps:
 * 1. Organization Details (name, tier, contact)
 * 2. Owner Account (email, password, name)
 * 3. Subscription Settings (duration, limits)
 * 4. Review & Create (confirm and send welcome email)
 */

import React, { useState } from 'react';
import {
  Building2,
  User,
  Calendar,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  X,
  Copy,
  Check,
  CheckCircle
} from 'lucide-react';

// Inline temp-password modal — mirrors the pattern in UserManagementPanel.jsx
export const TempPasswordModal = ({ orgName, ownerEmail, tempPassword, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for browsers without clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = tempPassword;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Organisation Created</h2>
              <p className="text-sm text-gray-600">Share these credentials securely with the owner</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Organisation:</span>
              <span className="text-sm font-medium text-gray-900">{orgName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Owner email:</span>
              <span className="text-sm font-medium text-gray-900">{ownerEmail}</span>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm font-medium text-amber-800 mb-2">Temporary Password</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white border border-amber-300 rounded px-3 py-2 font-mono text-lg text-gray-900 select-all">
                {tempPassword}
              </code>
              <button
                onClick={handleCopy}
                className="p-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                title="Copy password"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-amber-700 mt-2">
              This password is temporary. The user must change it on first login.
            </p>
          </div>

          <div className="text-sm text-gray-600">
            <p className="font-medium mb-1">Next steps:</p>
            <ol className="list-decimal list-inside space-y-1 text-gray-500">
              <li>Share the email and password securely with the owner</li>
              <li>They log in and will be prompted to set a new password</li>
            </ol>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

const SUBSCRIPTION_TIERS = {
  starter: {
    name: 'Starter',
    max_users: 5,
    max_vessels: 2,
    features: ['Email notifications', 'Basic support']
  },
  standard: {
    name: 'Standard',
    max_users: 10,
    max_vessels: 5,
    features: ['Email notifications', 'Priority support', 'Advanced reports']
  },
  professional: {
    name: 'Professional',
    max_users: 25,
    max_vessels: 15,
    features: ['Email notifications', '24/7 support', 'Advanced reports', 'API access']
  },
  enterprise: {
    name: 'Enterprise',
    max_users: 100,
    max_vessels: 50,
    features: ['Everything', 'Dedicated account manager', 'Custom integrations']
  }
};

export const OrganizationOnboardingWizard = ({ isOpen, onClose, onSuccess, onSubmit }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);


  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Organization Details
    org_name: '',
    org_tier: 'standard',
    contact_email: '',
    contact_phone: '',

    // Step 2: Owner Account
    owner_name: '',
    owner_email: '',
    owner_password: '',
    owner_phone: '',

    // Step 3: Subscription Settings
    subscription_duration: '1-year',
    max_users: 10,
    max_vessels: 5
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const calculateSubscriptionEndDate = () => {
    const start = new Date();
    switch (formData.subscription_duration) {
      case '1-month':
        return new Date(start.setMonth(start.getMonth() + 1));
      case '6-months':
        return new Date(start.setMonth(start.getMonth() + 6));
      case '1-year':
        return new Date(start.setFullYear(start.getFullYear() + 1));
      case '2-years':
        return new Date(start.setFullYear(start.getFullYear() + 2));
      default:
        return null; // lifetime
    }
  };

  const validateStep = (step) => {
    setError(null);

    switch (step) {
      case 1:
        if (!formData.org_name.trim()) {
          setError('Organization name is required');
          return false;
        }
        if (!formData.contact_email.trim()) {
          setError('Contact email is required');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
          setError('Invalid contact email format');
          return false;
        }
        return true;

      case 2:
        if (!formData.owner_name.trim()) {
          setError('Owner name is required');
          return false;
        }
        if (!formData.owner_email.trim()) {
          setError('Owner email is required');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.owner_email)) {
          setError('Invalid owner email format');
          return false;
        }
        return true;

      case 3:
        if (formData.max_users < 1) {
          setError('Max users must be at least 1');
          return false;
        }
        if (formData.max_vessels < 1) {
          setError('Max vessels must be at least 1');
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // FT-105 FIX: Use the onSubmit callback instead of deprecated Supabase API
      // The parent component passes createOrganization from useAdminOrganizations hook
      // which uses azureApiClient.adminCreateOrganization()
      if (!onSubmit) {
        throw new Error('Organization creation handler not provided');
      }

      // FT-105 FIX: Build organization data in FLAT format expected by Azure API
      // API expects: { organizationName, ownerEmail, ownerName, ownerRole }
      const orgData = {
        organizationName: formData.org_name,
        ownerEmail: formData.owner_email,
        ownerName: formData.owner_name,
        ownerRole: 'admin'
      };

      // Call the parent's createOrganization handler (from useAdminOrganizations)
      // Returns { organization, tempPassword, owner } after FT-368
      const result = await onSubmit(orgData);

      if (!result) {
        throw new Error('Failed to create organization');
      }

      // Pass tempPassword to parent via onSuccess — parent owns the modal so it
      // survives the wizard unmounting (the hook's fetchOrganizations sets isLoading=true
      // which would destroy any in-wizard state before it could render).
      if (onSuccess) {
        onSuccess({
          organization: result.organization,
          owner: result.owner || null,
          tempPassword: result.tempPassword || '(not returned by server)'
        });
      }
    } catch (err) {
      console.error('Error creating organization:', err);
      setError(err.message || 'Failed to create organization. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedTier = SUBSCRIPTION_TIERS[formData.org_tier];
  const endDate = calculateSubscriptionEndDate();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 text-white p-6 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="h-6 w-6" />
            <div>
              <h2 className="text-xl font-bold">Create Organization</h2>
              <p className="text-sm text-slate-300">Step {currentStep} of 4</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`flex items-center ${step < 4 ? 'flex-1' : ''}`}
              >
                <div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm
                    ${currentStep >= step ? 'bg-cyan-600 text-white' : 'bg-slate-200 text-slate-500'}
                  `}
                >
                  {step}
                </div>
                {step < 4 && (
                  <div
                    className={`
                      h-1 flex-1 mx-2
                      ${currentStep > step ? 'bg-cyan-600' : 'bg-slate-200'}
                    `}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-slate-600">
            <span>Organization</span>
            <span>Owner</span>
            <span>Subscription</span>
            <span>Review</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Organization Details */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Organization Details</h3>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Organization Name *
                </label>
                <input
                  type="text"
                  value={formData.org_name}
                  onChange={(e) => updateFormData('org_name', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="e.g., Demo Fleet Ltd"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Subscription Tier *
                </label>
                <select
                  value={formData.org_tier}
                  onChange={(e) => {
                    updateFormData('org_tier', e.target.value);
                    const tier = SUBSCRIPTION_TIERS[e.target.value];
                    updateFormData('max_users', tier.max_users);
                    updateFormData('max_vessels', tier.max_vessels);
                  }}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                >
                  {Object.entries(SUBSCRIPTION_TIERS).map(([key, tier]) => (
                    <option key={key} value={key}>
                      {tier.name} - {tier.max_users} users, {tier.max_vessels} vessels
                    </option>
                  ))}
                </select>
                <div className="mt-2 p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-1">Includes:</p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    {selectedTier.features.map((feature, idx) => (
                      <li key={idx}>• {feature}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Contact Email *
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => updateFormData('contact_email', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="admin@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => updateFormData('contact_phone', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="+44 1234 567890"
                />
              </div>
            </div>
          )}

          {/* Step 2: Owner Account */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Owner Account Details</h3>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Owner Full Name *
                </label>
                <input
                  type="text"
                  value={formData.owner_name}
                  onChange={(e) => updateFormData('owner_name', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Owner Email *
                </label>
                <input
                  type="email"
                  value={formData.owner_email}
                  onChange={(e) => updateFormData('owner_email', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="john@company.com"
                />
                <p className="mt-1 text-sm text-slate-500">This will be their login username</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Temporary Password
                </label>
                <p className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 text-sm">
                  Generated securely by the server on creation
                </p>
                <p className="mt-1 text-sm text-slate-500">The password will be displayed after the organisation is created so you can share it with the owner.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Owner Phone
                </label>
                <input
                  type="tel"
                  value={formData.owner_phone}
                  onChange={(e) => updateFormData('owner_phone', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="+44 7700 900123"
                />
              </div>
            </div>
          )}

          {/* Step 3: Subscription Settings */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Subscription Settings</h3>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Subscription Start Date
                </label>
                <input
                  type="text"
                  value={new Date().toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-slate-50"
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Subscription Duration
                </label>
                <select
                  value={formData.subscription_duration}
                  onChange={(e) => updateFormData('subscription_duration', e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                >
                  <option value="1-month">1 Month</option>
                  <option value="6-months">6 Months</option>
                  <option value="1-year">1 Year</option>
                  <option value="2-years">2 Years</option>
                  <option value="lifetime">Lifetime</option>
                </select>
                {endDate && (
                  <p className="mt-1 text-sm text-slate-600">
                    End Date: {endDate.toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                )}
              </div>

              <div className="border-t border-slate-200 pt-4 mt-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Usage Limits (can be changed later)</h4>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Maximum User Logins
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => updateFormData('max_users', Math.max(1, formData.max_users - 1))}
                      className="w-10 h-10 flex items-center justify-center bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors text-lg font-bold"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={formData.max_users}
                      onChange={(e) => updateFormData('max_users', Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-24 px-4 py-2 border border-slate-300 rounded-lg text-center focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                      min="1"
                    />
                    <button
                      type="button"
                      onClick={() => updateFormData('max_users', formData.max_users + 1)}
                      className="w-10 h-10 flex items-center justify-center bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors text-lg font-bold"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Maximum Vessels
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => updateFormData('max_vessels', Math.max(1, formData.max_vessels - 1))}
                      className="w-10 h-10 flex items-center justify-center bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors text-lg font-bold"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      value={formData.max_vessels}
                      onChange={(e) => updateFormData('max_vessels', Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-24 px-4 py-2 border border-slate-300 rounded-lg text-center focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                      min="1"
                    />
                    <button
                      type="button"
                      onClick={() => updateFormData('max_vessels', formData.max_vessels + 1)}
                      className="w-10 h-10 flex items-center justify-center bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors text-lg font-bold"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review & Create */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Review & Confirm</h3>

              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Organization Details
                  </h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-slate-600">Name:</dt>
                      <dd className="font-medium">{formData.org_name}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-600">Tier:</dt>
                      <dd className="font-medium">{SUBSCRIPTION_TIERS[formData.org_tier].name}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-600">Contact:</dt>
                      <dd className="font-medium">{formData.contact_email}</dd>
                    </div>
                    {formData.contact_phone && (
                      <div className="flex justify-between">
                        <dt className="text-slate-600">Phone:</dt>
                        <dd className="font-medium">{formData.contact_phone}</dd>
                      </div>
                    )}
                  </dl>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Owner Account
                  </h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-slate-600">Name:</dt>
                      <dd className="font-medium">{formData.owner_name}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-600">Email:</dt>
                      <dd className="font-medium">{formData.owner_email}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-600">Role:</dt>
                      <dd className="font-medium">OWNER</dd>
                    </div>
                    <div className="flex justify-between items-center">
                      <dt className="text-slate-600">Temp Password:</dt>
                      <dd className="text-xs text-slate-500 italic">Generated on creation</dd>
                    </div>
                  </dl>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Subscription
                  </h4>
                  <dl className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <dt className="text-slate-600">Tier:</dt>
                      <dd className="font-medium">{SUBSCRIPTION_TIERS[formData.org_tier].name}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-600">Duration:</dt>
                      <dd className="font-medium">{formData.subscription_duration}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-slate-600">Limits:</dt>
                      <dd className="font-medium">{formData.max_users} users, {formData.max_vessels} vessels</dd>
                    </div>
                  </dl>
                </div>

                <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                  <p className="text-sm text-cyan-800">
                    After creation, the temporary password will be displayed for you to share securely with the owner.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-50 p-6 rounded-b-2xl border-t border-slate-200 flex items-center justify-between">
          <div>
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                disabled={isSubmitting}
                className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Cancel
            </button>

            {currentStep < 4 ? (
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors flex items-center gap-2 font-medium"
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors flex items-center gap-2 font-medium disabled:bg-slate-400"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Create & Send
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationOnboardingWizard;
